"use client";

import * as React from "react";
import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible";

import { resolveRender } from "../../lib/resolve-render";

/**
 * Migrated from `@radix-ui/react-collapsible` to Base UI.
 *
 * The public API of every export here is unchanged, deliberately: 53 files
 * consume this component and the migration is worth nothing if it also has to
 * be a 53-file rewrite. Three Base UI differences are absorbed inside these
 * wrappers rather than pushed onto callers.
 *
 *  1. **`asChild` → `render`.** 39 call sites pass it, mostly to make a
 *     `<Button>` or a `<CardHeader>` the trigger. `resolveRender` translates,
 *     so they keep working untouched.
 *  2. **`Content` → `Panel`.** Base UI renamed the part. `CollapsibleContent`
 *     keeps its name and its `data-slot="collapsible-content"`, so callers and
 *     the `[&_[data-slot=…]]` selectors that reach into it are unaffected.
 *  3. **`forceMount` → `keepMounted`.** Radix's name for keeping the panel in
 *     the DOM while closed. One call site uses it.
 *  4. **`onOpenChange` gains a second argument.** Base UI passes
 *     `eventDetails` alongside the boolean; forwarding it would widen the
 *     callback every caller is typed against, for a value none of them want.
 *
 * One difference is **not** absorbable, because it names a DOM attribute that
 * callers style through directly. Radix puts `data-state="open|closed"` on every
 * part; Base UI splits it — the Root and Panel carry `data-open` / `data-closed`,
 * but the **Trigger** carries only `data-panel-open`, present when open with no
 * closed counterpart. Four files style through it and are updated in the same
 * commit; the two keyed on *closed* had to be inverted, since there is no
 * closed attribute on the trigger to match.
 *
 * A behavioural difference worth recording, since it is invisible until a test
 * asserts on it: while closed, Radix keeps the panel `<div>` mounted with
 * `hidden=""` and empties its children, whereas Base UI removes the node
 * (`keepMounted` defaults to false). The panel's *content* is unreachable
 * either way — which is what callers query — so the default is kept rather than
 * forcing `keepMounted` to imitate Radix's spare div.
 */
function Collapsible({
  onOpenChange,
  ...props
}: Omit<CollapsiblePrimitive.Root.Props, "onOpenChange"> & {
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <CollapsiblePrimitive.Root
      data-slot="collapsible"
      onOpenChange={(open) => onOpenChange?.(open)}
      {...props}
    />
  );
}

function CollapsibleTrigger({
  asChild,
  render,
  children,
  ...props
}: CollapsiblePrimitive.Trigger.Props & { asChild?: boolean }) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <CollapsiblePrimitive.Trigger
      data-slot="collapsible-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </CollapsiblePrimitive.Trigger>
  );
}

function CollapsibleContent({
  asChild,
  forceMount,
  render,
  children,
  ...props
}: CollapsiblePrimitive.Panel.Props & {
  asChild?: boolean;
  /**
   * Radix's spelling of Base UI's `keepMounted`: keep the panel in the DOM
   * while closed instead of removing it. One call site uses it
   * (`RebateAgreementDetailPage`), pairing it with a `data-[closed]:hidden`
   * class to do the hiding in CSS.
   */
  forceMount?: boolean;
}) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <CollapsiblePrimitive.Panel
      data-slot="collapsible-content"
      {...(forceMount ? { keepMounted: true } : {})}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </CollapsiblePrimitive.Panel>
  );
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
